import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, TextField, Dialog, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Alert, CircularProgress, Tabs, Tab, IconButton, Tooltip, LinearProgress, List, ListItem, ListItemText, ListItemIcon, FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails, Divider, CardActions, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel
} from '@mui/material';

import {
  Speed as SpeedIcon, Memory as MemoryIcon, Storage as StorageIcon, NetworkCheck as NetworkIcon, Assessment as AssessmentIcon, Refresh as RefreshIcon, PlayArrow as PlayArrowIcon, Stop as StopIcon, Clear as ClearIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Info as InfoIcon, ExpandMore as ExpandMoreIcon, Storage as CacheIcon, Storage as DatabaseIcon, Timeline as TimelineIcon, BugReport as BugReportIcon, Settings as SettingsIcon, TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon, Speed as SpeedDialIcon
} from '@mui/icons-material';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';

interface CacheStats {
  size: number;
  max_size: number;
  hits: number;
  misses: number;
  hit_rate: number;
  evictions: number;
  sets: number;
  deletes: number;
  uptime_seconds: number;
  tags: string[];
}

interface DatabaseStats {
  connection_pool: {
    pool_size: number;
    checked_in: number;
    checked_out: number;
    overflow: number;
    invalid: number;
    total_connections: number;
  };
  query_performance: {
    total_queries: number;
    avg_execution_time_ms: number;
    slow_queries: number;
    slow_query_percentage: number;
    query_types: Record<string, number>;
    table_performance: Record<string, any>;
  };
}

interface PerformanceSummary {
  cpu?: Record<string, any>;
  memory?: Record<string, any>;
  disk?: Record<string, any>;
  network?: Record<string, any>;
  database?: Record<string, any>;
  cache?: Record<string, any>;
}

interface LoadTestResult {
  test_name: string;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  success_rate: number;
  avg_response_time_ms: number;
  min_response_time_ms: number;
  max_response_time_ms: number;
  requests_per_second: number;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  errors: string[];
}

interface OptimizationRecommendation {
  type: string;
  severity: string;
  message: string;
  recommendation: string;
}

interface SystemHealth {
  status: string;
  warnings: string[];
  cache: CacheStats;
  database: DatabaseStats;
  performance: PerformanceSummary;
}

const PerformanceDashboard: React.FC = () => {
  const { token } = useAuth();
  const apiBase = getApiUrl();
  const defaultLoadTestUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/api/v1/health` : '/api/v1/health';
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [databaseStats, setDatabaseStats] = useState<DatabaseStats | null>(null);
  const [performanceSummary, setPerformanceSummary] = useState<PerformanceSummary | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [optimizationOverview, setOptimizationOverview] = useState<any>(null);
  const [recommendations, setRecommendations] = useState<OptimizationRecommendation[]>([]);
  const [slowQueries, setSlowQueries] = useState<any[]>([]);
  const [cacheKeys, setCacheKeys] = useState<string[]>([]);
  const [loadTestResult, setLoadTestResult] = useState<LoadTestResult | null>(null);
  
  // Dialog states
  const [loadTestDialog, setLoadTestDialog] = useState(false);
  const [cacheKeysDialog, setCacheKeysDialog] = useState(false);
  const [clearMetricsDialog, setClearMetricsDialog] = useState(false);
  
  // Load test form
  const [loadTestConfig, setLoadTestConfig] = useState('medium');
  const [loadTestUrl, setLoadTestUrl] = useState(defaultLoadTestUrl);
  const [runningLoadTest, setRunningLoadTest] = useState(false);
  
  // Monitoring state
  const [monitoringStatus, setMonitoringStatus] = useState<any>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);

  const fetchData = async (endpoint: string) => {
    try {
      const response = await fetch(`${apiBase}/performance/${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [
        cacheData,
        dbData,
        perfData,
        healthData,
        overviewData,
        recommendationsData,
        slowQueriesData,
        monitoringData
      ] = await Promise.all([
        fetchData('cache/stats'),
        fetchData('database/stats'),
        fetchData('summary?hours=24'),
        fetchData('system/health'),
        fetchData('optimization/overview'),
        fetchData('recommendations'),
        fetchData('database/slow-queries?limit=10'),
        fetchData('monitoring/status')
      ]);
      
      setCacheStats(cacheData);
      setDatabaseStats(dbData);
      setPerformanceSummary(perfData);
      setSystemHealth(healthData);
      setOptimizationOverview(overviewData);
      setRecommendations(recommendationsData.recommendations || []);
      setSlowQueries(slowQueriesData.slow_queries || []);
      setMonitoringStatus(monitoringData);
      setIsMonitoring(monitoringData.is_monitoring);
      
    } catch (error) {
      setError('Failed to load dashboard data');
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startMonitoring = async () => {
    try {
      await fetch(`${apiBase}/performance/monitoring/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setIsMonitoring(true);
      loadDashboardData();
    } catch (error) {
      console.error('Error starting monitoring:', error);
    }
  };

  const stopMonitoring = async () => {
    try {
      await fetch(`${apiBase}/performance/monitoring/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setIsMonitoring(false);
      loadDashboardData();
    } catch (error) {
      console.error('Error stopping monitoring:', error);
    }
  };

  const clearCache = async () => {
    try {
      await fetch(`${apiBase}/performance/cache/clear`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      loadDashboardData();
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  };

  const runLoadTest = async () => {
    setRunningLoadTest(true);
    try {
      const response = await fetch(`${apiBase}/performance/load-test?test_config=${loadTestConfig}&target_url=${encodeURIComponent(loadTestUrl)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setLoadTestResult(data.data);
      setLoadTestDialog(false);
    } catch (error) {
      console.error('Error running load test:', error);
      setError('Failed to run load test');
    } finally {
      setRunningLoadTest(false);
    }
  };

  const getCacheKeys = async () => {
    try {
      const keys = await fetchData('cache/keys');
      setCacheKeys(keys.keys || []);
      setCacheKeysDialog(true);
    } catch (error) {
      console.error('Error fetching cache keys:', error);
    }
  };

  const clearMetrics = async () => {
    try {
      await Promise.all([
        fetch(`${apiBase}/performance/cache/clear`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${apiBase}/performance/database/clear-metrics`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${apiBase}/performance/clear-metrics`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
      ]);
      setClearMetricsDialog(false);
      loadDashboardData();
    } catch (error) {
      console.error('Error clearing metrics:', error);
    }
  };

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'degraded': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const renderCacheTab = () => (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Cache Performance</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={getCacheKeys}
            sx={{ mr: 1 }}
          >
            View Keys
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ClearIcon />}
            onClick={clearCache}
          >
            Clear Cache
          </Button>
        </Box>
      </Box>

      {cacheStats && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Cache Statistics</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Hit Rate:</Typography>
                  <Typography variant="h6" color={cacheStats.hit_rate > 70 ? 'success.main' : 'warning.main'}>
                    {cacheStats.hit_rate}%
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Size:</Typography>
                  <Typography>{cacheStats.size} / {cacheStats.max_size}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Hits:</Typography>
                  <Typography>{cacheStats.hits}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Misses:</Typography>
                  <Typography>{cacheStats.misses}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Evictions:</Typography>
                  <Typography>{cacheStats.evictions}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Cache Hit Rate</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={cacheStats.hit_rate}
                      color={cacheStats.hit_rate > 70 ? 'success' : 'warning'}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="h6">{cacheStats.hit_rate}%</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {cacheStats.hit_rate > 70 ? 'Good cache performance' : 'Consider optimizing cache usage'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderDatabaseTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Database Performance</Typography>
      
      {databaseStats && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Connection Pool</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Total Connections:</Typography>
                  <Typography>{databaseStats.connection_pool.total_connections}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Checked Out:</Typography>
                  <Typography>{databaseStats.connection_pool.checked_out}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Overflow:</Typography>
                  <Typography color={databaseStats.connection_pool.overflow > 0 ? 'warning.main' : 'success.main'}>
                    {databaseStats.connection_pool.overflow}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Query Performance</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Total Queries:</Typography>
                  <Typography>{databaseStats.query_performance.total_queries}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Avg Response Time:</Typography>
                  <Typography>{databaseStats.query_performance.avg_execution_time_ms.toFixed(2)}ms</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Slow Queries:</Typography>
                  <Typography color={databaseStats.query_performance.slow_queries > 0 ? 'warning.main' : 'success.main'}>
                    {databaseStats.query_performance.slow_queries}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Slow Queries</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Execution Time (ms)</TableCell>
                        <TableCell>Query Type</TableCell>
                        <TableCell>Table</TableCell>
                        <TableCell>Rows Affected</TableCell>
                        <TableCell>Timestamp</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {slowQueries.map((query, index) => (
                        <TableRow key={index}>
                          <TableCell>{query.execution_time_ms.toFixed(2)}</TableCell>
                          <TableCell>{query.query_type}</TableCell>
                          <TableCell>{query.table_name}</TableCell>
                          <TableCell>{query.rows_affected}</TableCell>
                          <TableCell>{new Date(query.timestamp).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderPerformanceTab = () => (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Performance Monitoring</Typography>
        <Box>
          <Button
            variant={isMonitoring ? "contained" : "outlined"}
            color={isMonitoring ? "error" : "primary"}
            startIcon={isMonitoring ? <StopIcon /> : <PlayArrowIcon />}
            onClick={isMonitoring ? stopMonitoring : startMonitoring}
            sx={{ mr: 1 }}
          >
            {isMonitoring ? 'Stop Monitoring' : 'Start Monitoring'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ClearIcon />}
            onClick={() => setClearMetricsDialog(true)}
          >
            Clear Metrics
          </Button>
        </Box>
      </Box>

      {performanceSummary && (
        <Grid container spacing={3}>
          {performanceSummary.cpu && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>CPU Usage</Typography>
                  <Typography variant="h4" color="primary">
                    {performanceSummary.cpu.cpu_usage?.latest?.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average: {performanceSummary.cpu.cpu_usage?.avg?.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {performanceSummary.memory && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Memory Usage</Typography>
                  <Typography variant="h4" color="primary">
                    {performanceSummary.memory.memory_usage?.latest?.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available: {performanceSummary.memory.memory_available_gb?.latest?.toFixed(1)} GB
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {performanceSummary.disk && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Disk Usage</Typography>
                  <Typography variant="h4" color="primary">
                    {performanceSummary.disk.disk_usage?.latest?.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Free: {performanceSummary.disk.disk_free_gb?.latest?.toFixed(1)} GB
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {performanceSummary.network && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Network I/O</Typography>
                  <Typography variant="body2">
                    Sent: {(performanceSummary.network.network_bytes_sent?.latest / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                  <Typography variant="body2">
                    Received: {(performanceSummary.network.network_bytes_recv?.latest / (1024 * 1024)).toFixed(2)} MB
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );

  const renderLoadTestTab = () => (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Load Testing</Typography>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={() => setLoadTestDialog(true)}
        >
          Run Load Test
        </Button>
      </Box>

      {loadTestResult && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Load Test Results</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Test Configuration</Typography>
                <Typography variant="h6">{loadTestResult.test_name}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                <Typography variant="h6" color={loadTestResult.success_rate > 95 ? 'success.main' : 'warning.main'}>
                  {loadTestResult.success_rate}%
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Avg Response Time</Typography>
                <Typography variant="h6">{loadTestResult.avg_response_time_ms}ms</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="body2" color="text.secondary">Requests/Second</Typography>
                <Typography variant="h6">{loadTestResult.requests_per_second}</Typography>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">Details</Typography>
              <Typography variant="body2">
                Total: {loadTestResult.total_requests} | 
                Successful: {loadTestResult.successful_requests} | 
                Failed: {loadTestResult.failed_requests} | 
                Duration: {loadTestResult.duration_seconds}s
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const renderSystemHealthTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>System Health</Typography>
      
      {systemHealth && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Chip
                    label={systemHealth.status.toUpperCase()}
                    color={getHealthStatusColor(systemHealth.status) as any}
                    sx={{ mr: 2 }}
                  />
                  <Typography variant="h6">Overall System Status</Typography>
                </Box>
                
                {systemHealth.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Warnings:</Typography>
                    <List dense>
                      {systemHealth.warnings.map((warning, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <WarningIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={warning} />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Optimization Score</Typography>
                {optimizationOverview && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h4" color="primary" sx={{ mr: 2 }}>
                        {optimizationOverview.optimization_score}/100
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={optimizationOverview.optimization_score}
                        color={optimizationOverview.optimization_score > 80 ? 'success' : 'warning'}
                        sx={{ flex: 1, height: 20, borderRadius: 10 }}
                      />
                    </Box>
                    
                    {optimizationOverview.issues.length > 0 && (
                      <Alert severity="info">
                        <Typography variant="subtitle2">Issues Found:</Typography>
                        <List dense>
                          {optimizationOverview.issues.map((issue: string, index: number) => (
                            <ListItem key={index} sx={{ py: 0 }}>
                              <ListItemIcon sx={{ minWidth: 30 }}>
                                <InfoIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={issue} />
                            </ListItem>
                          ))}
                        </List>
                      </Alert>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderRecommendationsTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Optimization Recommendations</Typography>
      
      {recommendations.length > 0 ? (
        <List>
          {recommendations.map((rec, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <Chip
                    label={rec.severity.toUpperCase()}
                    color={getSeverityColor(rec.severity) as any}
                    size="small"
                    sx={{ mr: 2 }}
                  />
                  <Typography variant="subtitle1" sx={{ flex: 1 }}>
                    {rec.message}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Recommendation:</strong> {rec.recommendation}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </List>
      ) : (
        <Alert severity="success">
          <Typography>No optimization recommendations at this time. System is performing well!</Typography>
        </Alert>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Performance Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadDashboardData}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Cache" icon={<CacheIcon />} />
          <Tab label="Database" icon={<DatabaseIcon />} />
          <Tab label="Performance" icon={<TimelineIcon />} />
          <Tab label="Load Testing" icon={<SpeedDialIcon />} />
          <Tab label="System Health" icon={<AssessmentIcon />} />
          <Tab label="Recommendations" icon={<BugReportIcon />} />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {activeTab === 0 && renderCacheTab()}
        {activeTab === 1 && renderDatabaseTab()}
        {activeTab === 2 && renderPerformanceTab()}
        {activeTab === 3 && renderLoadTestTab()}
        {activeTab === 4 && renderSystemHealthTab()}
        {activeTab === 5 && renderRecommendationsTab()}
      </Box>

      {/* Load Test Dialog */}
      <Dialog open={loadTestDialog} onClose={() => setLoadTestDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run Load Test</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Test Configuration</InputLabel>
            <Select
              value={loadTestConfig}
              onChange={(e) => setLoadTestConfig(e.target.value)}
              label="Test Configuration"
            >
              <MenuItem value="light">Light (10 users, 60s)</MenuItem>
              <MenuItem value="medium">Medium (50 users, 120s)</MenuItem>
              <MenuItem value="heavy">Heavy (100 users, 180s)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Target URL"
            value={loadTestUrl}
            onChange={(e) => setLoadTestUrl(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadTestDialog(false)}>Cancel</Button>
          <Button
            onClick={runLoadTest}
            variant="contained"
            disabled={runningLoadTest}
            startIcon={runningLoadTest ? <CircularProgress size={20} /> : <PlayArrowIcon />}
          >
            {runningLoadTest ? 'Running...' : 'Start Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cache Keys Dialog */}
      <Dialog open={cacheKeysDialog} onClose={() => setCacheKeysDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Cache Keys</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Total Keys: {cacheKeys.length}
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {cacheKeys.map((key, index) => (
              <Typography key={index} variant="body2" sx={{ fontFamily: 'monospace', mb: 1 }}>
                {key}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCacheKeysDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Clear Metrics Dialog */}
      <Dialog open={clearMetricsDialog} onClose={() => setClearMetricsDialog(false)}>
        <DialogTitle>Clear All Metrics</DialogTitle>
        <DialogContent>
          <Typography>
            This will clear all cache entries, database metrics, and performance metrics. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearMetricsDialog(false)}>Cancel</Button>
          <Button onClick={clearMetrics} color="warning" variant="contained">
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PerformanceDashboard;
